<script setup lang="ts">
import { CloudUpload, FolderSync, Info, MoreVertical, Pencil, Trash2 } from '@lucide/vue'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu, RowActionReveal } from 'catalyst-vue'
import { useControlSize } from '../../lib/library-layout'

/**
 * 卡片動作選單。顯現行為完全交給套件的 row-action-reveal 樣式：
 * 細指標且視窗至少 64rem 時 hover 或 focus 才顯現，其餘情況常駐。
 * 前提是祖先帶有 data-row-action-reveal-scope。
 *
 * 定位交給呼叫端的 class：書架卡片浮在封面角落，列表列是列內的一般 flex 項目。
 */
defineProps<{ title: string; canBackup?: boolean; canRelink?: boolean }>()
defineEmits<{ select: [key: 'info' | 'organize' | 'backup' | 'relink' | 'remove'] }>()
const controlSize = useControlSize()
</script>

<template>
  <RowActionReveal>
    <Dropdown>
      <DropdownButton plain :size="controlSize" class="comic-manage" :aria-label="`${title} 的作品選項`">
        <MoreVertical :size="17" aria-hidden="true" />
      </DropdownButton>
      <DropdownMenu anchor="bottom end">
        <DropdownItem @click="$emit('select', 'info')">
          <Info data-slot="icon" :size="16" aria-hidden="true" />
          <DropdownLabel>作品資訊</DropdownLabel>
        </DropdownItem>
        <DropdownItem @click="$emit('select', 'organize')">
          <Pencil data-slot="icon" :size="16" aria-hidden="true" />
          <DropdownLabel>整理作品</DropdownLabel>
        </DropdownItem>
        <!-- 只有本地範圍且尚未備份的作品才有備份入口；已備份的再傳一次只會建立第二份副本。 -->
        <DropdownItem v-if="canBackup" @click="$emit('select', 'backup')">
          <CloudUpload data-slot="icon" :size="16" aria-hidden="true" />
          <DropdownLabel>備份到私人雲端</DropdownLabel>
        </DropdownItem>
        <!-- 只有本機檔案遺失的作品才有：把它接到搬走或改名後的新位置，資料全部保留。 -->
        <DropdownItem v-if="canRelink" @click="$emit('select', 'relink')">
          <FolderSync data-slot="icon" :size="16" aria-hidden="true" />
          <DropdownLabel>更新位置</DropdownLabel>
        </DropdownItem>
        <!-- 刪除放在最後、與其他項目隔開：它是破壞性的，走的是與整理對話框同一條確認。 -->
        <DropdownItem @click="$emit('select', 'remove')">
          <Trash2 data-slot="icon" :size="16" aria-hidden="true" />
          <DropdownLabel>刪除作品</DropdownLabel>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  </RowActionReveal>
</template>
